/**
 * DriveItemMenu — kebab-icon menu with per-item file/folder actions.
 *
 * Lives at the right edge of each drive item row. Primary actions (Open /
 * Gallery / Share) stay as buttons next to it; this menu holds Library and
 * the secondary actions admins reach for less often.
 *
 * Items vary by type:
 *   folder: Library • Rename • Move • Copy URL • Delete
 *   pdf:    Open • Share • Download • Rename • Move • Copy URL • Properties • Delete
 *   image:  Download • Rename • Move • Copy URL • Properties • Delete
 *   other:  Download • Rename • Move • Copy URL • Properties • Delete
 *
 * The page wires onAction to a single handler that switches on the action
 * id and runs the right operation.
 */

import { useId, useState, type MouseEvent } from 'react';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

export type DriveItemAction =
  | 'library'
  | 'rename'
  | 'move'
  | 'copy-url'
  | 'properties'
  | 'delete'
  | 'download';

export interface DriveItemMenuProps {
  /** Human-readable item name used to distinguish repeated menu buttons. */
  itemName: string;
  /** Whether the row is a folder (limits which actions show). */
  isFolder: boolean;
  /** Whether the row's path is a PDF (controls Properties affordance). */
  isPdf?: boolean;
  canOpenLibrary?: boolean;
  canDownload?: boolean;
  canRename?: boolean;
  canMove?: boolean;
  canCopyUrl?: boolean;
  canViewProperties?: boolean;
  canDelete?: boolean;
  /** Disable destructive items + multi-step ops. */
  disabled?: boolean;
  /** Fires when the user picks an action. */
  onAction: (action: DriveItemAction) => void;
}

export function DriveItemMenu({
  itemName,
  isFolder,
  isPdf,
  canOpenLibrary = isFolder,
  canDownload = !isFolder,
  canRename = true,
  canMove = true,
  canCopyUrl = true,
  canViewProperties = !isFolder,
  canDelete = true,
  disabled,
  onAction,
}: DriveItemMenuProps) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const menuId = `drive-item-menu-${useId().replace(/:/g, '')}`;
  const hasPrimaryItems = (isFolder && canOpenLibrary) || (!isFolder && canDownload);
  const hasManageItems = canRename || canMove || canCopyUrl || (!isFolder && canViewProperties);
  const hasAnyItem = hasPrimaryItems || hasManageItems || canDelete;

  if (!hasAnyItem) return null;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
  };
  const handleClose = (event?: MouseEvent) => {
    event?.stopPropagation();
    setAnchor(null);
  };
  const handlePick = (action: DriveItemAction) => (event: MouseEvent) => {
    event.stopPropagation();
    setAnchor(null);
    onAction(action);
  };

  return (
    <>
      <Tooltip title={`More actions for ${itemName}`}>
        <IconButton
          size="small"
          onClick={handleOpen}
          disabled={disabled}
          aria-label={`More actions for ${itemName}`}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
          aria-controls={open ? menuId : undefined}
        >
          <MoreVertRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => handleClose()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ id: menuId }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {isFolder && canOpenLibrary && (
          <MenuItem onClick={handlePick('library')}>
            <ListItemIcon><PictureAsPdfRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Library</ListItemText>
          </MenuItem>
        )}
        {!isFolder && canDownload && (
          <MenuItem onClick={handlePick('download')}>
            <ListItemIcon><DownloadRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        )}

        {canRename && (
          <MenuItem onClick={handlePick('rename')}>
            <ListItemIcon><DriveFileRenameOutlineRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rename...</ListItemText>
          </MenuItem>
        )}
        {canMove && (
          <MenuItem onClick={handlePick('move')}>
            <ListItemIcon><DriveFileMoveRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Move...</ListItemText>
          </MenuItem>
        )}
        {canCopyUrl && (
          <MenuItem onClick={handlePick('copy-url')}>
            <ListItemIcon><ContentCopyRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy {isFolder ? 'folder path' : 'file URL'}</ListItemText>
          </MenuItem>
        )}
        {!isFolder && canViewProperties && (
          <MenuItem onClick={handlePick('properties')}>
            <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Properties{isPdf ? '' : ''}</ListItemText>
          </MenuItem>
        )}

        {canDelete && <Divider />}

        {canDelete && (
          <MenuItem onClick={handlePick('delete')} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
